import React, { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Check, History, MessageSquare, Plus, Search, Trash2 } from "lucide-react";
import { useMainStore } from "@/stores";
import {
  useConversationThreadMessages,
  useConversationThreads,
  useCreateConversationThread,
  useDeleteConversationThread,
  useSendConversationChatMessage,
} from "@/features/accounts/hooks/useAccounts";
import ChatHeader from "@/shared/components/ui/ChatBot/ChatHeader";
import ChatPrompts from "@/shared/components/ui/ChatBot/ChatPrompts";
import ChatContainer from "@/shared/components/ui/PromptKit/ChatContainer";
import Message from "@/shared/components/ui/PromptKit/Message";
import PromptInput from "@/shared/components/ui/PromptKit/PromptInput";
import Loader from "@/shared/components/ui/PromptKit/Loader";
import CommonInput from "@/shared/components/ui/Input/CommonInput";
import SecondaryButton from "@/shared/components/ui/Button/SecondaryButton";
import { AI_PROMPTS_CONVERSATION } from "@/shared/utils/aiConfig";

const STREAMING_ID = "assistant-streaming";

const hasData = (step) => Object.keys(step.data || {}).length > 0;

// Fold a progress step into the live trace. Mirrored by _fold_progress in
// ai_backend/api/routers/chat.py, so a saved reply shows this same trace.
function foldProgressStep(steps, step) {
  const next = [...steps];
  if (step.status === "done" && (step.tool_name || step.title)) {
    const i = next.findIndex(
      (s) =>
        s.status === "running" &&
        (s.agent || "manager") === (step.agent || "manager") &&
        (s.tool_name === step.tool_name || s.title === step.title),
    );
    if (i !== -1) {
      if (!(hasData(next[i]) && hasData(step))) {
        next[i] = {
          ...next[i],
          ...step,
          data: hasData(step) ? step.data : next[i].data,
          status: "done",
        };
        return next;
      }
      // Both carry a payload (the command, then its artifact): keep both.
      next[i] = { ...next[i], status: "done" };
    }
  }
  if (step.status === "running") {
    return [
      ...next.map((s) => (s.status === "running" ? { ...s, status: "done" } : s)),
      step,
    ];
  }
  return [...next, step];
}

function ThreadHistory({ activeThreadId, onDelete, onSelect, query, setQuery, threads }) {
  const term = query.trim().toLowerCase();
  const filtered = threads.filter((thread) =>
    (thread.title || "").toLowerCase().includes(term),
  );

  return (
    <div className="absolute right-0 top-10 z-20 w-[320px] overflow-hidden rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-xl">
      <div className="p-2 border-b border-gray-100 dark:border-gray-800">
        <CommonInput
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search chat history..."
          icon={<Search size={14} className="text-gray-400" />}
          wrapperClassName="w-full min-w-0"
          className="text-xs"
        />
      </div>
      <div className="max-h-[380px] overflow-y-auto p-2">
        <p className="px-2 pb-2 text-xs font-semibold uppercase text-gray-400 dark:text-gray-400">
          Chat history
        </p>
        {filtered.map((thread) => {
          const active = thread.id === activeThreadId;
          return (
            <div
              key={thread.id}
              onClick={() => onSelect(thread.id)}
              className={`group flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-sm cursor-pointer ${active ? "bg-secondary dark:bg-gray-800 text-white dark:text-gray-100 font-medium" : "text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
                }`}
            >
              <div className="flex items-center gap-2 min-w-0 flex-1">
                {active ? <Check size={15} className="shrink-0 text-white" /> : <MessageSquare size={15} className="shrink-0 text-gray-400" />}
                <span className="min-w-0 flex-1 truncate text-xs">{thread.title}</span>
              </div>
              <SecondaryButton
                icon={<Trash2 size={13} className="text-gray-400 hover:text-red-600" />}
                // HeroUI press events already stop the click reaching the row.
                onClick={() => onDelete(thread.id)}
                className="!p-1 !h-6 !min-w-0 hidden group-hover:flex border-none shadow-none bg-transparent hover:bg-gray-200 dark:hover:bg-gray-700 shrink-0"
              />
            </div>
          );
        })}
        {filtered.length === 0 ? (
          <p className="px-3 py-8 text-center text-sm text-gray-400">
            No matching threads
          </p>
        ) : null}
      </div>
    </div>
  );
}

const ChatBot = ({ companyName = "", className = "" }) => {
  const { user } = useMainStore();
  const userId = user?.id;
  const queryClient = useQueryClient();

  const [activeThreadId, setActiveThreadId] = useState(null);
  const [isDraftThread, setIsDraftThread] = useState(true);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyQuery, setHistoryQuery] = useState("");
  const containerRef = useRef(null);
  const previousThreadIdRef = useRef(null);
  const abortRef = useRef(null);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  const threadsQuery = useConversationThreads({ userId, companyName });
  const messagesQuery = useConversationThreadMessages({
    userId,
    threadId: activeThreadId,
    enabled: Boolean(activeThreadId),
  });
  const createThread = useCreateConversationThread();
  const deleteThread = useDeleteConversationThread();
  const sendMessage = useSendConversationChatMessage();

  const threads = React.useMemo(
    () => threadsQuery.data?.threads || [],
    [threadsQuery.data],
  );

  // Load persisted messages when switching threads.
  useEffect(() => {
    const threadChanged = previousThreadIdRef.current !== activeThreadId;
    previousThreadIdRef.current = activeThreadId;
    if (!activeThreadId) {
      if (threadChanged) setMessages([]);
      return;
    }
    // TEMP [stop-debug]: remove once the stop flow is confirmed.
    console.log("[stop-debug] server sync", {
      threadId: activeThreadId,
      loading,
      count: messagesQuery.data?.messages?.length,
      last: messagesQuery.data?.messages?.at(-1)?.agent_result?.stopped
        ? "stopped"
        : messagesQuery.data?.messages?.at(-1)?.role,
    });
    if (loading) return;
    if (messagesQuery.data?.messages) {
      setMessages(messagesQuery.data.messages);
      if (threadChanged) {
        requestAnimationFrame(() => containerRef.current?.scrollToBottom());
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeThreadId, messagesQuery.data]);

  // Default to the most recent thread on first load (or a draft when empty).
  useEffect(() => {
    if (!threadsQuery.isSuccess || activeThreadId || !isDraftThread) return;
    if (threads.length > 0 && messages.length === 0) {
      setActiveThreadId(threads[0].id);
      setIsDraftThread(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadsQuery.isSuccess]);

  const handleNewThread = useCallback(() => {
    setActiveThreadId(null);
    setIsDraftThread(true);
    setMessages([]);
    setHistoryOpen(false);
  }, []);

  const handleSelectThread = useCallback((threadId) => {
    setActiveThreadId(threadId);
    setIsDraftThread(false);
    setHistoryOpen(false);
  }, []);

  const handleDeleteThread = useCallback(
    async (threadId) => {
      try {
        await deleteThread.mutateAsync({ user_id: userId, thread_id: threadId });
      } catch (e) {
        console.error("Failed to delete thread:", e);
        return;
      }
      if (threadId === activeThreadId) {
        const remaining = threads.filter((t) => t.id !== threadId);
        if (remaining.length > 0) {
          handleSelectThread(remaining[0].id);
        } else {
          handleNewThread();
        }
      }
    },
    [activeThreadId, deleteThread, handleNewThread, handleSelectThread, threads, userId],
  );

  const handleStreamChunk = useCallback((chunk) => {
    if (chunk.role === "user") {
      setMessages((prev) => {
        const optimisticIndex = prev.findIndex((message) => message._optimistic);
        if (optimisticIndex === -1) return [...prev, chunk];
        const updated = [...prev];
        updated[optimisticIndex] = chunk;
        return updated;
      });
      return;
    }
    if (chunk.type === "delta" || chunk.role === "delta") {
      setMessages((prev) => {
        const idx = prev.findIndex((m) => m.id === STREAMING_ID);
        if (idx === -1) {
          return [
            ...prev,
            {
              id: STREAMING_ID,
              role: "model",
              content: chunk.delta || "",
              progress_steps: [],
            },
          ];
        }
        const updated = [...prev];
        const existing = updated[idx];
        updated[idx] = {
          ...existing,
          content: chunk.replace
            ? chunk.delta || ""
            : (existing.content || "") + (chunk.delta || ""),
        };
        return updated;
      });
      return;
    }
    const isProgressChunk =
      chunk.type === "progress" ||
      chunk.role === "progress" ||
      chunk.type === "step" ||
      chunk.role === "step" ||
      chunk.type === "agent" ||
      chunk.type === "thought" ||
      chunk.type === "reasoning" ||
      Boolean(chunk.progress) ||
      Boolean(chunk.progress_step) ||
      Boolean(chunk.step);

    if (isProgressChunk) {
      const stepData = chunk.progress || chunk.progress_step || chunk.step;
      const step = typeof stepData === "object" && stepData !== null
        ? stepData
        : {
          title: chunk.title || chunk.content || chunk.text || "Agent working...",
          detail: chunk.detail,
          status: chunk.status || "running",
          tool_name: chunk.tool_name || chunk.tool,
          agent: chunk.agent || "manager",
          data: chunk.data,
        };

      setMessages((prev) => {
        const idx = prev.findIndex((m) => m.id === STREAMING_ID);
        if (idx === -1) {
          return [
            ...prev,
            {
              id: STREAMING_ID,
              role: "model",
              content: "",
              progress_steps: [step],
            },
          ];
        }
        const updated = [...prev];
        updated[idx] = {
          ...prev[idx],
          progress_steps: foldProgressStep(prev[idx].progress_steps || [], step),
        };
        return updated;
      });
      return;
    }
    if (chunk.role === "model") {
      setMessages((prev) => {
        const streamingMsg = prev.find((m) => m.id === STREAMING_ID);
        const withoutStreaming = prev.filter((m) => m.id !== STREAMING_ID);
        const steps =
          chunk.progress_steps?.length
            ? chunk.progress_steps
            : chunk.agent_result?.progress_steps?.length
              ? chunk.agent_result.progress_steps
              : streamingMsg?.progress_steps || [];
        return [
          ...withoutStreaming,
          {
            ...chunk,
            progress_steps: steps,
          },
        ];
      });
    }
  }, []);

  const handleSend = useCallback(
    async (promptText = null) => {
      const content = (promptText || input).trim();
      if (!content || loading || !userId) return;

      setInput("");
      setLoading(true);
      const controller = new AbortController();
      abortRef.current = controller;
      const now = Date.now();
      setMessages((prev) => [
        ...prev.filter((m) => m.id !== STREAMING_ID),
        {
          id: `user-${now}`,
          role: "user",
          content,
          _optimistic: true,
        },
        {
          id: STREAMING_ID,
          role: "model",
          content: "",
          progress_steps: [],
        },
      ]);
      requestAnimationFrame(() => containerRef.current?.scrollToBottom());

      let threadId = activeThreadId;
      let stoppedMessages = null;
      try {
        if (!threadId) {
          const created = await createThread.mutateAsync({
            user_id: userId,
            company_name: companyName || undefined,
          });
          threadId = created?.thread?.id;
          if (!threadId) throw new Error("Thread creation failed");
          setActiveThreadId(threadId);
          setIsDraftThread(false);
        }

        await sendMessage.mutateAsync({
          user_id: userId,
          thread_id: threadId,
          prompt: content,
          company_name: companyName || undefined,
          signal: controller.signal,
          onStreamChunk: handleStreamChunk,
        });
      } catch (e) {
        if (controller.signal.aborted) {
          // Stopped by the seller: freeze the reply as it stands, partial text
          // and agent trace intact. The server saves the same reply.
          stoppedMessages = messagesRef.current.map((m) =>
            m.id === STREAMING_ID
              ? { ...m, id: `stopped-${now}`, agent_result: { stopped: true } }
              : m,
          );
          setMessages(stoppedMessages);
          // TEMP [stop-debug]: remove once the stop flow is confirmed.
          const frozen = stoppedMessages.find((m) => m.id === `stopped-${now}`);
          console.log("[stop-debug] stopped", {
            threadId,
            steps: frozen?.progress_steps?.length,
            chars: frozen?.content?.length,
          });
        } else {
          console.error("Failed to send conversation message:", e);
          setMessages((prev) => [
            ...prev.filter((m) => m.id !== STREAMING_ID),
            {
              id: `error-${Date.now()}`,
              role: "model",
              content:
                "Something went wrong while sending that message. Please try again.",
            },
          ]);
        }
      } finally {
        abortRef.current = null;
        setLoading(false);
        queryClient.invalidateQueries({
          queryKey: ["conversationThreads", userId],
          exact: false,
        });
        if (threadId) {
          const messagesKey = ["conversationThreadMessages", userId, threadId];
          if (stoppedMessages) {
            // The server saves a stopped reply only as the request drops, so
            // a refetch now can land first and wipe the reply off screen.
            // Cache what is on screen; the next open refetches the saved copy.
            await queryClient.cancelQueries({ queryKey: messagesKey });
            queryClient.setQueryData(messagesKey, { messages: stoppedMessages });
            queryClient.invalidateQueries({
              queryKey: messagesKey,
              refetchType: "none",
            });
          } else {
            queryClient.invalidateQueries({ queryKey: messagesKey });
          }
        }
      }
    },
    [
      activeThreadId,
      companyName,
      createThread,
      handleStreamChunk,
      input,
      loading,
      queryClient,
      sendMessage,
      userId,
    ],
  );

  const handleStop = useCallback(() => abortRef.current?.abort(), []);

  const AllPrompts = (props) => (
    <ChatPrompts prompts={AI_PROMPTS_CONVERSATION} {...props} />
  );

  const showEmptyState = messages.length === 0 && !loading;

  return (
    <div
      className={`flex h-full min-h-0 flex-col overflow-hidden rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-3 font-sans ${className}`}
    >
      {/* Compact recent conversations */}
      <div className="relative flex flex-shrink-0 items-center gap-2 border-b border-gray-100 dark:border-gray-800 pb-2">
        <span className="hidden shrink-0 text-[11px] font-medium text-gray-400 sm:block">
          Recents
        </span>
        <div className="flex min-w-0 flex-1 items-center gap-1 overflow-x-auto scrollbar-hide">
          {(isDraftThread || threads.length === 0) && (
            <div
              aria-current="page"
              className="flex h-8 max-w-[180px] shrink-0 items-center gap-2 rounded-lg bg-secondary px-2.5 text-xs font-medium text-white shadow-sm"
            >
              <MessageSquare size={14} className="shrink-0 text-white/80" />
              <span className="truncate">New chat</span>
            </div>
          )}
          {threads.slice(0, 4).map((thread) => {
            const active = thread.id === activeThreadId && !isDraftThread;
            return (
              <button
                type="button"
                key={thread.id}
                title={thread.title}
                onClick={() => handleSelectThread(thread.id)}
                aria-current={active ? "page" : undefined}
                className={`flex h-8 max-w-[180px] shrink-0 items-center gap-2 rounded-lg px-2.5 text-left text-xs transition ${active
                  ? "bg-secondary font-medium text-white shadow-sm"
                  : "border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                  }`}
              >
                <MessageSquare
                  size={14}
                  className={`shrink-0 ${active ? "text-white/80" : "text-gray-400 dark:text-gray-400"}`}
                />
                <span className="block min-w-0 truncate">{thread.title}</span>
              </button>
            );
          })}
        </div>
        <SecondaryButton
          title=""
          icon={<Plus size={14} />}
          onClick={handleNewThread}
          className="!min-w-0 !w-7 !h-7 !p-0 shrink-0 flex justify-center items-center rounded-lg"
          aria-label="New chat"
        />
        <SecondaryButton
          title=""
          icon={<History size={14} />}
          onClick={() => setHistoryOpen((open) => !open)}
          className={`!min-w-0 !w-7 !h-7 !p-0 shrink-0 flex justify-center items-center rounded-lg ${historyOpen ? "!bg-secondary !text-white !border-secondary" : ""
            }`}
          aria-label="Chat history"
        />
        {historyOpen && (
          <ThreadHistory
            activeThreadId={activeThreadId}
            onDelete={handleDeleteThread}
            onSelect={handleSelectThread}
            query={historyQuery}
            setQuery={setHistoryQuery}
            threads={threads}
          />
        )}
      </div>

      {/* Messages */}
      <ChatContainer ref={containerRef} className="flex flex-col gap-4 pb-3 pt-4">
        {showEmptyState && (
          <div className="flex flex-col items-center gap-4 pt-8">
            <ChatHeader />
            <AllPrompts setInput={setInput} getChat={handleSend} />
          </div>
        )}

        <div className="flex w-full flex-col gap-5 px-1">
          {messages.map((msg, index) => {
            const hasLaterUserMessage = messages
              .slice(index + 1)
              .some((later) => later.role === "user");
            return (
              <Message
                key={msg.id || index}
                message={msg}
                isStreaming={msg.id === STREAMING_ID}
                userName={user?.name}
                onOptionSelect={handleSend}
                showFollowUp={!hasLaterUserMessage && !loading}
                disabled={loading}
              />
            );
          })}
          {loading && !messages.some((m) => m.id === STREAMING_ID) && (
            <div className="ml-12">
              <Loader label="Thinking" />
            </div>
          )}
        </div>
      </ChatContainer>

      {/* Input */}
      <div className="mt-auto flex-shrink-0 pt-3">
        <PromptInput
          input={input}
          setInput={setInput}
          onSend={handleSend}
          isGenerating={loading}
          onStop={handleStop}
        />
      </div>
    </div>
  );
};

export default ChatBot;
